/**
 * BackToTop — floating button that appears after the user scrolls down
 * and smooth-scrolls back to the top on click.
 *
 * Mounted once at the App root so every route on every environment
 * (public landing, authenticated app, admin pages) gets it for free.
 */
import React, { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

const SHOW_AFTER_PX = 400;

export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > SHOW_AFTER_PX);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();  // run once in case the user lands mid-page (e.g. via #anchor)
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    // Respect the user's prefers-reduced-motion setting.
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Back to top"
      title="Back to top"
      data-testid="back-to-top-button"
      className={`fixed bottom-6 right-20 z-50 h-11 w-11 rounded-full bg-[#2C302E] text-white shadow-lg flex items-center justify-center transition-all duration-200 hover:bg-[#1b1e1c] hover:scale-105 ${
        visible
          ? "opacity-90 pointer-events-auto translate-y-0"
          : "opacity-0 pointer-events-none translate-y-3"
      }`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}
